import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { X, CheckCircle, AlertCircle, Info, AlertTriangle } from 'lucide-react';

type ToastType = 'success' | 'error' | 'info' | 'warning';

interface ToastAction {
  label: string;
  onClick: () => void;
}

interface ToastOptions {
  /** T.ex. "Ångra" — bättre än en bekräftelsedialog för ändringar som går att backa. */
  action?: ToastAction;
  /** Millisekunder. Notiser med åtgärd visas längre som standard. */
  duration?: number;
}

interface Toast {
  id: string;
  message: string;
  type: ToastType;
  action?: ToastAction;
}

interface ToastContextType {
  toast: (message: string, type?: ToastType, options?: ToastOptions) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timers = useRef(new Map<string, ReturnType<typeof setTimeout>>());

  const removeToast = useCallback((id: string) => {
    const timer = timers.current.get(id);
    if (timer) clearTimeout(timer);
    timers.current.delete(id);
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const toast = useCallback((message: string, type: ToastType = 'info', options: ToastOptions = {}) => {
    const id = Math.random().toString(36).substring(2, 9);
    setToasts((prev) => [...prev.slice(-2), { id, message, type, action: options.action }]);
    const duration = options.duration ?? (options.action ? 7000 : 4000);
    timers.current.set(id, setTimeout(() => removeToast(id), duration));
  }, [removeToast]);

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      {/* På mobil ligger notiserna ovanför den svävande navigeringen. */}
      <div
        aria-live="polite"
        className="fixed left-4 right-4 bottom-24 md:left-auto md:bottom-24 md:right-6 z-[160] flex flex-col items-stretch md:items-end gap-2 pointer-events-none"
      >
        {toasts.map((t) => (
          <div
            key={t.id}
            role={t.type === 'error' ? 'alert' : 'status'}
            className={`
              pointer-events-auto flex items-center gap-3 pl-4 pr-2 py-2.5 rounded-xl shadow-lg border animate-fade-in-up
              md:min-w-[320px] md:max-w-md backdrop-blur-md
              ${t.type === 'success' ? 'bg-white/95 border-tone-olive/25 text-tone-olive' : ''}
              ${t.type === 'error' ? 'bg-white/95 border-tone-rust/25 text-tone-rust' : ''}
              ${t.type === 'warning' ? 'bg-white/95 border-tone-caramel/25 text-tone-caramel' : ''}
              ${t.type === 'info' ? 'bg-white/95 border-line text-ink-900' : ''}
            `}
          >
            {t.type === 'success' && <CheckCircle size={18} className="text-tone-olive shrink-0" aria-hidden="true" />}
            {t.type === 'error' && <AlertCircle size={18} className="text-tone-rust shrink-0" aria-hidden="true" />}
            {t.type === 'warning' && <AlertTriangle size={18} className="text-tone-caramel shrink-0" aria-hidden="true" />}
            {t.type === 'info' && <Info size={18} className="text-tone-latte shrink-0" aria-hidden="true" />}

            <p className="text-body font-bold flex-1 py-1">{t.message}</p>

            {t.action && (
              <button
                type="button"
                onClick={() => { t.action!.onClick(); removeToast(t.id); }}
                className="px-3 py-1.5 rounded-lg text-meta font-black text-ink-900 bg-paper-100 hover:bg-paper-200 transition-colors shrink-0"
              >
                {t.action.label}
              </button>
            )}

            <button
              type="button"
              onClick={() => removeToast(t.id)}
              aria-label="Stäng notis"
              className="p-1.5 text-current opacity-50 hover:opacity-100 transition-opacity shrink-0"
            >
              <X size={16} />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
};
